/**
 * ResolutionSelector Component
 *
 * Dropdown selector for resolution presets with custom option.
 * Shows aspect ratio preview for each resolution.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { imageGenerationAPI } from '../../services/imageGenerationAPI';
import type { ResolutionInfo } from '../../types';

interface ResolutionSelectorProps {
  value: string;
  customWidth?: number;
  customHeight?: number;
  onChange: (preset: string, width?: number, height?: number) => void;
  disabled?: boolean;
  /** Provider ID - resolutions are filtered per provider */
  provider?: string;
}

export function ResolutionSelector({
  value,
  customWidth,
  customHeight,
  onChange,
  disabled = false,
  provider,
}: ResolutionSelectorProps) {
  const { t } = useTranslation();
  const [resolutions, setResolutions] = useState<ResolutionInfo[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isCustom, setIsCustom] = useState(false);
  const [localWidth, setLocalWidth] = useState(customWidth || 1024);
  const [localHeight, setLocalHeight] = useState(customHeight || 1024);
  const [isLoading, setIsLoading] = useState(true);

  // Load resolutions from API (re-fetch when provider changes)
  useEffect(() => {
    const loadResolutions = async () => {
      setIsLoading(true);
      try {
        const response = await imageGenerationAPI.getResolutions(provider);
        if (response.success && response.data) {
          // Defensive: ensure we always have arrays
          const resData = response.data.resolutions;
          const catData = response.data.categories;
          setResolutions(Array.isArray(resData) ? resData : []);
          setCategories(Array.isArray(catData) ? catData : []);
        }
      } catch (error) {
        console.error('Failed to load resolutions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadResolutions();
  }, [provider]);

  // Auto-correct an out-of-range resolution once the provider's supported set
  // is known. Switching from an SDXL provider (e.g. 1152x896) to SD 1.5 leaves
  // a value the new provider rejects -> the backend raises "Invalid resolution"
  // and the generate call 502s. When the current preset isn't supported, fall
  // back to the first supported resolution (512x512 for SD 1.5). Custom sizes
  // are left untouched (validated server-side).
  useEffect(() => {
    if (isLoading || isCustom) return;
    const list = Array.isArray(resolutions) ? resolutions : [];
    if (list.length === 0) return;
    if (!list.some((r) => r.name === value)) {
      onChange(list[0].name);
    }
  }, [resolutions, value, isCustom, isLoading, onChange]);

  // Group resolutions by category (defensive: ensure both are arrays)
  const safeResolutions = Array.isArray(resolutions) ? resolutions : [];
  const safeCategories = Array.isArray(categories) ? categories : [];
  const groupedResolutions = safeCategories.reduce(
    (acc, category) => {
      acc[category] = safeResolutions.filter((r) => r.category === category);
      return acc;
    },
    {} as Record<string, ResolutionInfo[]>
  );

  const handleCustomChange = (width: number, height: number) => {
    setLocalWidth(width);
    setLocalHeight(height);
    onChange('custom', width, height);
  };

  const handlePresetChange = (preset: string) => {
    setIsCustom(preset === 'custom');
    if (preset === 'custom') {
      onChange('custom', localWidth, localHeight);
    } else {
      onChange(preset);
    }
  };

  return (
    <div className="space-y-4">
      <label className="form-label">
        {t('image.resolution.label', { defaultValue: 'Resolution' })}
      </label>

      {/* Preset Selector */}
      <div className="grid grid-cols-1 gap-4">
        <select
          value={isCustom ? 'custom' : value}
          onChange={(e) => handlePresetChange(e.target.value)}
          className="select"
          disabled={disabled || isLoading}
          data-testid="resolution-selector"
        >
          {isLoading ? (
            <option value="">{t('common.loading', { defaultValue: 'Loading...' })}</option>
          ) : (
            <>
              {safeCategories.map((category) => (
                <optgroup
                  key={category}
                  label={t(`image.resolution.categories.${category}`, {
                    defaultValue: category.charAt(0).toUpperCase() + category.slice(1),
                  })}
                >
                  {groupedResolutions[category]?.map((res) => (
                    <option key={res.name} value={res.name}>
                      {res.name} - {res.aspect_ratio || `${res.width}x${res.height}`}
                    </option>
                  ))}
                </optgroup>
              ))}
              <option value="custom">
                {t('image.resolution.custom', { defaultValue: 'Custom' })}
              </option>
            </>
          )}
        </select>

        {/* Custom Resolution Inputs */}
        {isCustom && (
          <div className="grid grid-cols-2 gap-4 p-4 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg">
            <div>
              <label htmlFor="custom-width" className="label-themed-sm">
                {t('image.resolution.width', { defaultValue: 'Width' })}
              </label>
              <input
                id="custom-width"
                type="number"
                min={512}
                max={2048}
                step={64}
                value={localWidth}
                onChange={(e) => handleCustomChange(parseInt(e.target.value) || 512, localHeight)}
                className="input"
                disabled={disabled}
                data-testid="custom-width-input"
              />
            </div>
            <div>
              <label htmlFor="custom-height" className="label-themed-sm">
                {t('image.resolution.height', { defaultValue: 'Height' })}
              </label>
              <input
                id="custom-height"
                type="number"
                min={512}
                max={2048}
                step={64}
                value={localHeight}
                onChange={(e) => handleCustomChange(localWidth, parseInt(e.target.value) || 512)}
                className="input"
                disabled={disabled}
                data-testid="custom-height-input"
              />
            </div>
            <p className="col-span-2 text-xs text-secondary-500 dark:text-secondary-400">
              {t('image.resolution.hint', {
                defaultValue: 'Values should be between 512 and 2048, in multiples of 64',
              })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResolutionSelector;
